import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { MediaCapture, MediaFile } from '@awesome-cordova-plugins/media-capture/ngx';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { ActionSheetController, Platform } from '@ionic/angular';
import { LAUNCH_FILE_DIRECTORY } from '@shared/constants';
import { ConfigService } from '@shared/services/core/config.service';

interface LocalFile {
	name: string;
	path: string;
	data: string;
}

@Component({
	selector: 'file-uploader',
	template: `
		<div class="relative block my-5">
			<ion-button [class]="buttonClass" (click)="selectImage()" expand="block">
				<ion-icon name="camera-outline"></ion-icon>
				&nbsp;{{ buttonText }}
			</ion-button>
			<div class="py-5 overflow-hidden" *ngFor="let file of images; let i = index">
				<div class="flex flex-wrap justify-center">
					<ion-button *ngIf="!hasUploaded" slot="end" fill="clear" (click)="uploadData()">
						<ion-icon slot="icon-only" class="mr-1" name="cloud-upload"></ion-icon>
						Upload
					</ion-button>
					<ion-button class="text-red-500" slot="end" fill="clear" (click)="deleteImage(file)">
						<ion-icon slot="icon-only" class="mr-1" name="trash"></ion-icon>
						Remove
					</ion-button>
				</div>
				<input #uploader id="singleFile" type="file" id="" class="hidden" />
				<ion-img *ngIf="!uploadVideo" class="rounded h-10/12 max-h-40 w-auto mb-10" [src]="file.data"></ion-img>
				<div *ngIf="uploadVideo && hasUploaded">
					<div class="bg-green-500 p-4 text-center text-sm text-white font-bold relative">
						<div>Video Uploaded</div>
					</div>
				</div>
				<div *ngIf="!uploadVideo && hasUploaded">
					<div class="bg-green-500 p-4 text-center text-sm text-white font-bold relative">
						<div>Image Successfully Uploaded</div>
					</div>
				</div>
			</div>
			<div *ngIf="!uploadVideo && hasUploaded && singleUpload && images.length === 0">
				<div class="flex flex-wrap justify-center">
					<ion-button class="text-red-500" slot="end" fill="clear" (click)="clearFiles(true)">
						<ion-icon slot="icon-only" class="mr-1" name="trash"></ion-icon>
						Remove
					</ion-button>
				</div>

				<div class="bg-green-500 p-4 text-center text-sm text-white font-bold relative">
					<div>Image Successfully Uploaded</div>
				</div>
			</div>
		</div>
	`,
})
export class FileUploaderComponent implements OnInit {
	public images: any[] = [];
	private savedImage = {};
	@Input() hasUploaded: boolean;
	@Input() buttonClass: string = '';
	@Input() directoryPrefix: string = '';
	@Input() singleUpload: boolean = true;
	@Input() uploadVideo: boolean = false;
	@Input() buttonText: string;
	@Input() clearOnLoad: boolean = false;
	@Input() instantUpload: boolean = false;
	@Output() onFileUpload: EventEmitter<any> = new EventEmitter();
	@Output() onFileRemove: EventEmitter<any> = new EventEmitter();
	@Output() fileDataLoaded: EventEmitter<any> = new EventEmitter();
	@ViewChild('uploader') fileInput: HTMLInputElement;

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		public readonly actionSheetCtrl: ActionSheetController,
		public readonly platform: Platform,
		private readonly env: ConfigService,
		private readonly mediaCapture: MediaCapture,
		private readonly fileReader: File,
		private readonly changeDetector: ChangeDetectorRef
	) {}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - ngOnINit
	 **-------------------------------------------------------------------------------------
	 */
	async ngOnInit() {
		if (this.clearOnLoad) this.clearFiles();
		this.loadFiles();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - openContact
	 **-------------------------------------------------------------------------------------
	 */

	async chooser() {
		const actionSheet = await this.actionSheetCtrl.create({
			header: 'File Upload Type',
			buttons: [
				{
					text: `Upload Picture`,
					icon: 'image',
					handler: () => {
						this.uploadVideo = false;
					},
				},
				{
					text: `Upload Video`,
					icon: 'videocam',
					handler: () => {
						this.uploadVideo = true;
					},
				},
				{
					text: 'Cancel',
					role: 'cancel',
					handler: () => {
						actionSheet.dismiss();
					},
				},
			],
		});

		await actionSheet.present();
		return await actionSheet.onDidDismiss();
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - loadFiles
	 **-------------------------------------------------------------------------------------
	 */
	async loadFiles() {
		this.images = [];
		Filesystem.readdir({
			path: `${LAUNCH_FILE_DIRECTORY}/${this.directoryPrefix}/`,
			directory: Directory.Data,
		}).then(
			result => {
				if (!result.files.length) return;
				this.loadFileData(result.files);
				this.changeDetector.detectChanges();
			},
			async err => {
				try {
					await Filesystem.mkdir({
						path: `${LAUNCH_FILE_DIRECTORY}/${this.directoryPrefix}/`,
						directory: Directory.Data,
						recursive: true,
					});
				} catch (e) {}
			}
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - loadFiles
	 **-------------------------------------------------------------------------------------
	 */
	async loadFileData(fileNames: any[]) {
		for (let f of fileNames) {
			const filePath = `${LAUNCH_FILE_DIRECTORY}/${this.directoryPrefix}/${f.name}`;
			const readFile = await Filesystem.readFile({
				path: filePath,
				directory: Directory.Data,
			});
			this.images.push({
				name: f,
				path: filePath,
				data: `data:image/jpeg;base64,${readFile.data}`,
			});
		}

		this.fileDataLoaded.emit(this.images);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - selectImage
	 **-------------------------------------------------------------------------------------
	 */
	async selectImage() {
		if (this.hasUploaded && this.singleUpload) return;
		const resp = await this.chooser();
		if (resp.role === 'cancel' || resp.role === 'backdrop') return;
		if (this.hasUploaded && this.singleUpload) return;
		if (this.singleUpload) {
			await this.clearFiles();
		}

		if (this.platform.is('cordova') && this.uploadVideo) {
			try {
				const file = (await this.mediaCapture.captureVideo()) as MediaFile[];
				if (file.length) {
					const videoBase64 = await this.fileToBase64(file[0]);
					this.saveFile(file[0], videoBase64);
				}
			} catch (e) {}
			return;
		}

		try {
			if (this.platform.is('cordova')) {
				try {
					const file = (await this.mediaCapture.captureImage()) as MediaFile[];
					if (file.length) {
						const imageBase64 = await this.fileToBase64(file[0]);
						this.saveFile(file[0], imageBase64);
					}
				} catch (e) {}
				return;
			}
			//web upload
			const image = await Camera.getPhoto({
				quality: 75,
				allowEditing: false,
				resultType: CameraResultType.DataUrl,
				source: this.env.get('production') ? CameraSource.Camera : CameraSource.Prompt,
			});
			if (image) {
				this.saveImage(image);
			}
		} catch (e) {}
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveImage
	 **-------------------------------------------------------------------------------------
	 */
	async saveImage(file) {
		const fileName = file.format;
		const base64Data = await this.getBase64Image(file.dataUrl);
		const savedFile = await Filesystem.writeFile({
			path: `${LAUNCH_FILE_DIRECTORY}/${this.directoryPrefix}/${fileName}`,
			data: base64Data,
			directory: Directory.Data,
		});
		this.hasUploaded = false;
		this.loadFiles();
		this.savedImage = {
			file,
			savedFile,
			fileData: {
				type: this.getMimeType(file.dataUrl),
				name: fileName,
				format: file.format,
			},
		};

		if (this.instantUpload) {
			this.uploadData();
		}
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveFile
	 **-------------------------------------------------------------------------------------
	 */
	async saveFile(file, base64Data) {
		const savedFile = await Filesystem.writeFile({
			path: `${LAUNCH_FILE_DIRECTORY}/${this.directoryPrefix}/${file.name}`,
			data: base64Data,
			directory: Directory.Data,
		});
		await this.fileReader.resolveLocalFilesystemUrl(savedFile.uri);
		this.hasUploaded = false;
		this.loadFiles();
		this.savedImage = { file: { dataUrl: base64Data }, savedFile, fileData: file };

		if (this.instantUpload) {
			this.uploadData();
		}
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getBase64Image
	 **-------------------------------------------------------------------------------------
	 */
	async getBase64Image(url): Promise<any> {
		const data = await fetch(url);
		const blob = await data.blob();
		return new Promise(resolve => {
			const reader = new FileReader();
			reader.readAsDataURL(blob);
			reader.onloadend = () => {
				const base64data = reader.result;
				resolve(base64data);
			};
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - uploadData
	 **-------------------------------------------------------------------------------------
	 */
	async uploadData() {
		if (!this.savedImage) {
			this.generateSavedImageFromLoadedImages();
		}

		this.onFileUpload.emit(this.savedImage);
		this.hasUploaded = true;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - generateSavedImageFromLoadedImages
	 **-------------------------------------------------------------------------------------
	 */
	generateSavedImageFromLoadedImages() {
		const image = this.images[0];
		let dataUrl = image.data;

		if (image.data.includes('data:image')) {
			dataUrl = image.data.split(',')[1];
		}

		const mimeType = this.getMimeType(image.data);
		const format = mimeType.split('/')[1];
		const imageData = {
			file: { dataUrl },
			savedFile: { uri: image.name.uri },
			fileData: {
				type: this.getMimeType(image.data),
				name: image.name.name,
				format,
				size: image.name.size,
			},
		};

		this.savedImage = imageData;
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getBase64Image
	 **-------------------------------------------------------------------------------------
	 */
	async deleteImage(file: LocalFile, loadFiles: boolean = false) {
		await Filesystem.deleteFile({
			directory: Directory.Data,
			path: file.path,
		});
		this.hasUploaded = false;
		this.loadFiles();
		this.onFileRemove.emit({ savedFile: `${Directory.Data}/${file.path}` });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clearFiles
	 **-------------------------------------------------------------------------------------
	 */
	public async clearFiles(emitEvent: boolean = false) {
		try {
			const filePath = `${LAUNCH_FILE_DIRECTORY}/${this.directoryPrefix}`;
			const readDir = await Filesystem.readdir({
				path: filePath,
				directory: Directory.Data,
			});

			readDir.files.map(async i => {
				try {
					await Filesystem.deleteFile({
						directory: Directory.Data,
						path: `${LAUNCH_FILE_DIRECTORY}/${this.directoryPrefix}/${i.name}`,
					});
				} catch (e) {}
			});
		} catch (e) {}

		if (emitEvent) {
			this.onFileRemove.emit();
		}
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - fileToBase64
	 **-------------------------------------------------------------------------------------
	 */
	async fileToBase64(file) {
		return new Promise(resolve => {
			(<any>window).resolveLocalFileSystemURL(file.localURL, async fileEntry => {
				Filesystem.readFile({ path: fileEntry.toNativeURL() }).then(async resp => {
					resolve(resp.data);
				});
			});
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getMimeType
	 **-------------------------------------------------------------------------------------
	 */
	private getMimeType(dataurl) {
		const arr = dataurl.split(','),
			mime = arr[0].match(/:(.*?);/)[1];
		return mime;
	}
}
